import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortfolioStats } from '../hooks/usePortfolioStats';
import AssetService from '../services/assetService';
import StockDetailsModal from '../components/StockDetailsModal';
import FloatingAIAssistant from '../components/FloatingAIAssistant';
import FloatingCompetitionButton from '../components/FloatingCompetitionButton';
import MobileTopBarClean from '../components/MobileTopBarClean';
import { useTranslation } from 'react-i18next';
import '../styles/global-white-backgrounds.css';
import '../styles/professional-investment-cards.css';
import '../styles/stocks-page.css';
import { useIsMobile } from '../hooks/useIsMobile';

const EtfIndicesPage = () => {
  const navigate = useNavigate();
  const { getHeaderStats } = usePortfolioStats();
  const { t } = useTranslation();
  const [etfs, setEtfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEtf, setSelectedEtf] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const [draggedEtf, setDraggedEtf] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [touchMoved, setTouchMoved] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isOverDropZone, setIsOverDropZone] = useState(false);
  const dragElementRefs = useState(() => new Map())[0];

  useEffect(() => {
    fetchTopETFs();
  }, []);


  // Setup non-passive touch events for mobile drag
  useEffect(() => {
    if (!isMobile) return;

    const handleTouchMove = (e) => {
      if (!touchStartPos || !draggedEtf) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.x);
      const deltaY = Math.abs(currentY - touchStartPos.y);
      
      // Very low threshold for easy mobile dragging
      if (deltaX > 5 || deltaY > 5) {
        if (!touchMoved) {
          setTouchMoved(true);
          setIsDragging(true);
        }
        
        // Update drag position for visual feedback
        const dragX = currentX - touchStartPos.x;
        const dragY = currentY - touchStartPos.y;
        setDragPosition({ x: dragX, y: dragY });
        
        // Check if dragging over drop zone for visual feedback
        const elementAtPoint = document.elementFromPoint(currentX, currentY);
        const overDropZone = elementAtPoint?.closest('.icon-container') || 
                           elementAtPoint?.closest('.floating-ai-assistant');
        setIsOverDropZone(!!overDropZone);
        
        // Add haptic feedback on iOS when over drop zone
        if (overDropZone && navigator.vibrate) {
          navigator.vibrate(10);
        }
        
        e.preventDefault(); // This will now work with non-passive events
        e.stopPropagation();
      }
    };

    // Add non-passive touch event listeners to all draggable elements
    dragElementRefs.forEach((element) => {
      if (element) {
        element.addEventListener('touchmove', handleTouchMove, { passive: false });
      }
    });

    return () => {
      dragElementRefs.forEach((element) => {
        if (element) {
          element.removeEventListener('touchmove', handleTouchMove);
        }
      });
    };
  }, [isMobile, touchStartPos, draggedEtf, touchMoved, dragElementRefs]);

  const fetchTopETFs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch ETFs using Polygon API with ETF filter
      const response = await AssetService.getAssets({
        market: 'stocks',
        type: 'ETF', 
        active: 'true',
        limit: '100', // Get more to find the highest value ones
        sort: 'ticker',
        order: 'asc'
      });
      
      if (response && response.results) {
        // Get detailed data for each ETF to find market cap
        const etfsWithDetails = await Promise.all(
          response.results.slice(0, 20).map(async (etf) => { // Limit to first 20 to avoid too many calls
            try {
              // Get detailed company info including market cap
              const detailResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${etf.ticker}`);
              const detailData = await detailResponse.json();
              
              // Get current price data 
              const priceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${etf.ticker}/prev`);
              const priceData = await priceResponse.json();
              
              const companyDetails = detailData.results || {};
              const currentData = priceData.results?.[0] || {};
              
              return {
                ticker: etf.ticker,
                name: etf.name || companyDetails.name || etf.ticker,
                currentPrice: currentData.c || 0,
                close: currentData.c || 0, // StockDetailsModal expects 'close'
                ask: currentData.h || 0, // Day high as ask
                bid: currentData.l || 0, // Day low as bid
                change: 0, // Will be calculated properly later
                changePercent: 0,
                dayHigh: currentData.h || 0,
                dayLow: currentData.l || 0,
                volume: currentData.v || 0,
                marketCap: companyDetails.market_cap || 0,
                logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${etf.ticker}/logo`,
                iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${etf.ticker}/icon`
              };
            } catch (error) {
              console.warn(`Failed to enhance data for ${etf.ticker}:`, error);
              return null;
            }
          })
        );
        
        // Filter out failed requests and sort by market cap
        const validEtfs = etfsWithDetails
          .filter(etf => etf && etf.marketCap > 0)
          .sort((a, b) => b.marketCap - a.marketCap)
          .slice(0, 5); // Get top 5 by market cap
        
        // If we don't have enough with market cap data, fall back to well-known ETFs
        if (validEtfs.length < 5) {
          console.warn('Not enough ETFs with market cap data, using fallback list');
          const fallbackTickers = ['SPY', 'QQQ', 'VTI', 'IWM', 'EFA'];
          
          const fallbackEtfs = await Promise.all(
            fallbackTickers.map(async (ticker) => {
              try {
                const detailResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${ticker}`);
                const detailData = await detailResponse.json();
                
                const priceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${ticker}/prev`);
                const priceData = await priceResponse.json();
                
                const companyDetails = detailData.results || {};
                const currentData = priceData.results?.[0] || {};
                
                return {
                  ticker: ticker,
                  name: companyDetails.name || ticker,
                  currentPrice: currentData.c || 0,
                  close: currentData.c || 0, // StockDetailsModal expects 'close'
                  ask: currentData.h || 0, // Day high as ask
                  bid: currentData.l || 0, // Day low as bid
                  change: 0,
                  changePercent: 0,
                  dayHigh: currentData.h || 0,
                  dayLow: currentData.l || 0,
                  volume: currentData.v || 0,
                  marketCap: companyDetails.market_cap || 0,
                  logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/logo`,
                  iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/icon`
                };
              } catch (error) {
                console.warn(`Failed to fetch fallback data for ${ticker}:`, error);
                return null;
              }
            })
          );
          
          setEtfs(fallbackEtfs.filter(etf => etf));
        } else {
          setEtfs(validEtfs);
        }
        
        setError(null);
      } else {
        throw new Error('No ETF data received');
      }
    } catch (error) {
      console.error('Error fetching ETFs:', error);
      setError('Failed to load ETF data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle ETF click - open stock details modal
  const handleEtfClick = (etf) => {
    setSelectedEtf(etf);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEtf(null);
  };

  return (
    <div className="dashboard-container jpx-style">
      <div className="main-content" style={{ marginLeft: 0, width: '100%' }}>
        {/* X BUTTON TO RETURN TO DASHBOARD */}
        <button className="modal-close" onClick={() => navigate('/dashboard')} style={{
          position: 'fixed',
          top: '120px',
          right: '20px',
          width: '40px',
          height: '40px',
          background: '#000000',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '24px',
          cursor: 'pointer',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease'
        }}>×</button>
        {/* Conditional Header - Mobile vs Desktop */}
        <MobileTopBarClean stats={getHeaderStats()} />


        {/* Main content */}
        <main className="dashboard-content" style={{
          marginTop: isMobile ? '75px' : '0'
        }}>
          {/* Error Display */}
          {error && (
            <div className="error-message">
              <span className="error-icon"></span>
              {error}
            </div>
          )}

          {/* ETF Indices Grid */}
          <section className="stocks-section">
            <div className="section-header">
              <h2 className="section-title">{t('assets:pages.etfIndices.sectionTitle')}</h2>
              <div className="section-subtitle">{t('assets:pages.etfIndices.sectionSubtitle')}</div>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>{t('assets:loading.etfData')}</p>
              </div>
            ) : (
              <div className="stocks-grid">
                {etfs.map((etf) => (
                  <div 
                    key={etf.ticker}
                    className="stock-card stocks"
                    onClick={(e) => {
                      // Don't trigger click if we're dragging
                      if (!touchMoved && !draggedEtf) {
                        handleEtfClick(etf);
                      }
                    }}
                  >
                    {/* ETF Logo - Draggable */}
                    <div 
                      ref={(el) => {
                        if (el) {
                          dragElementRefs.set(etf.ticker, el);
                        }
                      }}
                      className={`stock-logo draggable-logo ${
                        draggedEtf === etf.ticker ? 'dragging-active' : ''
                      } ${isDragging && draggedEtf === etf.ticker ? 'dragging-move' : ''}`}
                      draggable="true"
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          ticker: etf.ticker,
                          name: etf.name,
                          price: etf.close,
                          type: 'etf'
                        }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setDraggedEtf(etf.ticker);
                      }}
                      onDragEnd={(e) => {
                        setDraggedEtf(null);
                      }}
                      // Mobile touch support
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        const touch = e.touches[0];
                        setTouchStartPos({
                          x: touch.clientX,
                          y: touch.clientY,
                          time: Date.now(),
                          pageX: touch.pageX,
                          pageY: touch.pageY
                        });
                        setTouchMoved(false);
                        setIsDragging(false);
                        setDragPosition({ x: 0, y: 0 });
                        
                        // Provide immediate visual feedback
                        setDraggedEtf(etf.ticker);
                      }}
                      onTouchMove={(e) => {
                        // This is now handled by the non-passive event listener in useEffect
                        if (touchStartPos && draggedEtf === etf.ticker) {
                          e.stopPropagation();
                        }
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        
                        if (!touchStartPos) {
                          return;
                        }
                        
                        // Only trigger if user dragged to a drop zone
                        if (touchMoved) {
                          // Check if touch ended over the AI assistant drop zone
                          const touchEndX = e.changedTouches[0].clientX;
                          const touchEndY = e.changedTouches[0].clientY;
                          const elementAtEnd = document.elementFromPoint(touchEndX, touchEndY);
                          
                          // Look for the AI assistant drop zone or its children
                          const dropZone = elementAtEnd?.closest('.icon-container') || 
                                          elementAtEnd?.closest('.floating-ai-assistant');
                          
                          if (dropZone) {
                            console.log('✅ MOBILE DRAG: Dispatching event for', etf.ticker);
                            const event = new CustomEvent('tickerDropped', {
                              detail: {
                                ticker: etf.ticker,
                                name: etf.name,
                                price: etf.close,
                                type: 'etf'
                              }
                            });
                            window.dispatchEvent(event);
                            console.log('✅ MOBILE DRAG: Event dispatched successfully');
                          } else {
                            console.log('❌ MOBILE DRAG: Not over drop zone. Element:', elementAtEnd?.className);
                          }
                        }
                        
                        // Reset all touch state
                        setTouchStartPos(null);
                        setTouchMoved(false);
                        setDraggedEtf(null);
                        setIsDragging(false);
                        setDragPosition({ x: 0, y: 0 });
                        setIsOverDropZone(false);
                      }}
                      style={{ 
                        cursor: 'grab',
                        opacity: draggedEtf === etf.ticker ? (isDragging ? 0.8 : 0.9) : 1,
                        '--drag-x': isDragging && draggedEtf === etf.ticker ? `${dragPosition.x}px` : '0px',
                        '--drag-y': isDragging && draggedEtf === etf.ticker ? `${dragPosition.y}px` : '0px',
                        '--drag-scale': draggedEtf === etf.ticker ? 
                          (isDragging ? (isOverDropZone ? '1.25' : '1.15') : '1.05') : '1',
                        '--drag-rotate': isDragging && draggedEtf === etf.ticker ? '2deg' : '0deg',
                        boxShadow: draggedEtf === etf.ticker ? '0 8px 20px rgba(0,0,0,0.3)' : 'none',
                        transition: isDragging ? 'none' : 'all 0.2s ease',
                        touchAction: 'none',
                        WebkitUserDrag: 'element',
                        userSelect: 'none',
                        backgroundColor: draggedEtf === etf.ticker ? 
                          (isOverDropZone ? '#e0f2fe' : '#f0f0f0') : 'transparent',
                        borderRadius: '8px',
                        zIndex: draggedEtf === etf.ticker ? 1000 : 1
                      }}
                      title={isMobile ? t('assets:card.mobileDragHintEtf') : t('assets:card.dragHintEtf')}
                    >
                      <img 
                        src={etf.logoUrl}
                        alt={`${etf.name} logo`}
                        style={{ 
                          pointerEvents: 'none',
                          touchAction: 'none',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTouchCallout: 'none'
                        }}
                        onError={(e) => {
                          // First fallback: try icon instead of logo
                          if (e.target.src === etf.logoUrl) {
                            e.target.src = etf.iconUrl;
                          } else {
                            // Final fallback: placeholder with ticker letter
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23059669" rx="16"/><text x="50" y="60" font-family="Arial, sans-serif" font-size="36" font-weight="bold" fill="white" text-anchor="middle">' + etf.ticker[0] + '</text></svg>';
                          }
                        }}
                      />
                    </div>

                    {/* ETF Info */}
                    <div className="stock-info">
                      <div className="stock-header">
                        <h3 className="stock-ticker">{etf.ticker}</h3>
                        <div className="stock-name">{etf.name}</div>
                      </div>

                      <div className="stock-price">
                        <span className="current-price">${etf.currentPrice?.toFixed(2) || 'N/A'}</span>
                        <div className={`price-change ${etf.change >= 0 ? 'positive' : 'negative'}`}>
                          {etf.change >= 0 ? '+' : ''}{etf.change?.toFixed(2) || 'N/A'} 
                          ({etf.changePercent >= 0 ? '+' : ''}{etf.changePercent?.toFixed(2) || 'N/A'}%)
                        </div>
                      </div>

                      <div className="stock-metrics">
                        <div className="metric">
                          <span className="metric-label">Day High</span>
                          <span className="metric-value">${etf.dayHigh?.toFixed(2) || 'N/A'}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Day Low</span>
                          <span className="metric-value">${etf.dayLow?.toFixed(2) || 'N/A'}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Volume</span>
                          <span className="metric-value">{etf.volume?.toLocaleString() || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      className="stock-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEtfClick(etf);
                      }}
                    >
                      {t('assets:card.actions.viewDetails')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>

        {/* Stock Details Modal */}
        {modalOpen && selectedEtf && (
          <StockDetailsModal 
            stock={selectedEtf}
            isOpen={modalOpen}
            onClose={handleCloseModal}
          />
        )}

        {/* Floating AI Assistant */}
        <FloatingAIAssistant />

        {/* Floating Competition Button */}
        <FloatingCompetitionButton />
      </div>
    </div>
  );
};

export default EtfIndicesPage;